import React, { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import { IStock, useDeleteHsDataMutation } from '../../generated/graphql';
import DialogBox from '../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';

type IProps = {
  handleClose: () => void;
  selectedStock: IStock | null;
};

type IErrors = {
  deleteEndDate: string | null;
  deleteEndDateInvalidDate: string | null;
};

const defaultErrorsObj: IErrors = {
  deleteEndDate: null,
  deleteEndDateInvalidDate: null
};

const errorTexts: IErrors = {
  deleteEndDate: 'Delete End Date is required',
  deleteEndDateInvalidDate: 'Invalid Date Format'
};

const minDate = new Date(1970, 0, 1);
const deleteMinDateMessage = `date should be after ${minDate}`;

const DeleteHSDataForm: React.FunctionComponent<IProps> = ({
  handleClose,
  selectedStock
}: IProps) => {
  const [deleteEndDate, setDeleteEndDate] = useState<Date | null>(new Date());
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const { dispatch } = useContext(AppContext);
  const [deleteHSDATA] = useDeleteHsDataMutation();

  const handleCancel = () => {
    setDeleteEndDate(null);
    handleClose();
  };

  const getPureDate = (date: Date | null) => {
    const getMonthWithTwoDigit = (date: Date) => {
      return ('0' + (date?.getMonth() + 1)).slice(-2);
    };

    const getDateWithTwoDigit = (date: Date | null) => {
      return ('0' + date?.getDate()).slice(-2);
    };
    const pureDate = `${date?.getFullYear()}-${getMonthWithTwoDigit(
      date as Date
    )}-${getDateWithTwoDigit(date)}`;
    return pureDate;
  };

  const handleDeleteHSData = () => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `'Are you sure you want to delete History Stocks Data until ${getPureDate(
          deleteEndDate
        )} included ?'`,
        agreeAction: deleteHSData
      }
    });
  };

  const deleteHSData = async () => {
    const pureDate = getPureDate(deleteEndDate);
    const stock_id = selectedStock?.id;
    deleteHSDATA({
      variables: {
        id: stock_id as string,
        input: {
          endDate: pureDate
        }
      },
      update(_, { data }) {
        if (data?.result) {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Successfully deleted History Stocks Data for stock ${selectedStock?.name} until ${pureDate}`,
              autoHide: 4000
            }
          });
        }
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
    dispatch({ type: Actions.HideConfirmation });
  };

  const handleDeleteEndDateChange = (value: Date | null) => {
    if (value) {
      if (value < new Date(minDate)) {
        errorTexts.deleteEndDate = deleteMinDateMessage;
      } else if (errorTexts.deleteEndDate === deleteMinDateMessage) {
        errorTexts.deleteEndDate = null;
      }
      setFormErrors({ ...formErrors, deleteEndDate: null });
    } else {
      setFormErrors({ ...formErrors, deleteEndDate: errorTexts.deleteEndDate });
    }
    setDeleteEndDate(value);
  };
  const handleSubmit = () => {
    const errors = {
      deleteEndDate: null,
      deleteEndDateInvalidDate: null
    } as IErrors;
    let hasErrors = false;
    if (!deleteEndDate) {
      hasErrors = true;
      errors.deleteEndDate = errorTexts.deleteEndDate;
    } else {
      if (!Date.parse(deleteEndDate.toString())) {
        hasErrors = true;
        errors.deleteEndDate = errorTexts.deleteEndDateInvalidDate;
      }
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleDeleteHSData();
      handleClose();
    }
  };

  return (
    <DialogBox
      id="history-stocks-delete-form"
      width={375}
      title="Delete History Stocks Data"
      onClose={handleClose}
    >
      <Box sx={{ margin: '50px', display: 'flex', flexDirection: 'column', gap: '35px' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <EpraDatePicker
            id="delete-end-date-picker"
            label="End Date"
            value={deleteEndDate}
            onChange={(date) => handleDeleteEndDateChange(date)}
            errorMsg={formErrors.deleteEndDate}
            minDate={minDate}
            sx={{ width: '100%' }}
          />
        </LocalizationProvider>
        <Box className="centralize">
          <CancelButton id="cancel-delete-button" onClick={handleCancel} text="Close" />
          <SaveButton id="save-delete-button" onClick={handleSubmit} text="Submit" />
        </Box>
      </Box>
    </DialogBox>
  );
};
export default DeleteHSDataForm;
